import { signal } from "zyra";
import { Button, Card, Badge, Input } from "@zyra/ui";
import { UIPreview } from "./UIPreview";
import { UIFormExample } from "./UIFormExample";

export function UIShowcase() {
  const theme = signal("dark");
  const brand = signal("green");
  const feedback = signal("Try a button to see it respond.");
  return (
    <div
      class="ui-showcase"
      data-zyra-theme={theme.value}
      data-ui-brand={brand.value}
    >
      <div class="ui-gallery-intro">
        <div class="ui-gallery-kicker">
          <span /> THE COMPONENT COLLECTION
        </div>
        <h2>See it. Try it. Make it yours.</h2>
        <p>
          Working Zyra components, from the smallest button to a complete form.
          Change the theme, explore the states, and take the code with you.
        </p>
        <div class="ui-gallery-controls">
          <div class="ui-control-group" role="group" aria-label="Preview theme">
            <button
              aria-pressed={theme.value === "light"}
              onClick={() => {
                theme.value = "light";
              }}
            >
              Light
            </button>
            <button
              aria-pressed={theme.value === "dark"}
              onClick={() => {
                theme.value = "dark";
              }}
            >
              Dark
            </button>
          </div>
          <div class="ui-control-group" role="group" aria-label="Accent color">
            <button
              aria-pressed={brand.value === "green"}
              onClick={() => {
                brand.value = "green";
              }}
            >
              <i class="ui-color-dot ui-color-green" />
              Fern
            </button>
            <button
              aria-pressed={brand.value === "violet"}
              onClick={() => {
                brand.value = "violet";
              }}
            >
              <i class="ui-color-dot ui-color-violet" />
              Iris
            </button>
          </div>
          <span class="ui-gallery-count">7 components · fully interactive</span>
        </div>
      </div>

      <UIPreview
        id="ui-buttons"
        title="Buttons"
        description="Four variants. Three sizes. One consistent interaction."
        code={
          'import { Button } from "@zyra/ui";\n\n<Button variant="primary">Create project</Button>\n<Button variant="secondary">Learn more</Button>\n<Button variant="ghost">Cancel</Button>\n<Button variant="danger">Delete</Button>\n\n<Button size="sm">Small</Button>\n<Button size="md">Medium</Button>\n<Button size="lg">Large</Button>\n<Button disabled>Disabled</Button>'
        }
      >
        <div class="ui-specimen-label">VARIANTS</div>
        <div class="ui-component-row">
          <Button
            onClick={() => {
              feedback.value = "Primary action selected.";
            }}
          >
            Create project <span aria-hidden="true">↗</span>
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              feedback.value = "Secondary action selected.";
            }}
          >
            Learn more
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              feedback.value = "Action canceled.";
            }}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              feedback.value =
                "Danger variant selected — this preview deletes nothing.";
            }}
          >
            Delete
          </Button>
        </div>
        <div class="ui-specimen-label ui-specimen-divider">SIZES & STATES</div>
        <div class="ui-component-row">
          <Button
            size="sm"
            onClick={() => {
              feedback.value = "Small button selected.";
            }}
          >
            Small
          </Button>
          <Button
            size="md"
            onClick={() => {
              feedback.value = "Medium button selected.";
            }}
          >
            Medium
          </Button>
          <Button
            size="lg"
            onClick={() => {
              feedback.value = "Large button selected.";
            }}
          >
            Large
          </Button>
          <Button disabled>Disabled</Button>
        </div>
        <p role="status" class="ui-demo-feedback">
          {feedback.value}
        </p>
      </UIPreview>

      <UIPreview
        id="ui-badges"
        title="Badges"
        description="Small signals that give status a clear visual language."
        code={
          'import { Badge } from "@zyra/ui";\n\n<Badge>Draft</Badge>\n<Badge variant="success">Published</Badge>\n<Badge variant="warning">In review</Badge>'
        }
      >
        <div class="ui-status-examples">
          <div>
            <Badge>Draft</Badge>
            <span>Work in progress</span>
          </div>
          <div>
            <Badge variant="success">Published</Badge>
            <span>Ready for the world</span>
          </div>
          <div>
            <Badge variant="warning">In review</Badge>
            <span>Needs your attention</span>
          </div>
        </div>
      </UIPreview>

      <UIPreview
        id="ui-inputs"
        title="Input states"
        description="Clear labels, helpful feedback, and native form behavior."
        code={
          'import { Input } from "@zyra/ui";\n\n<label for="name">Project name</label>\n<Input id="name" placeholder="Your next idea" />\n\n<label for="email">Email</label>\n<Input id="email" type="email" aria-invalid="true"\n  aria-describedby="email-error" value="hello@" />\n<p id="email-error">Enter a complete email address.</p>\n\n<Input aria-label="Workspace ID" disabled value="zyra-workspace" />\n<Input aria-label="Enable notifications" type="checkbox" />'
        }
      >
        <div class="ui-fields-grid">
          <div>
            <label for="ui-state-default">Default</label>
            <Input id="ui-state-default" placeholder="Your next idea" />
            <small>Click or Tab into the field to see focus.</small>
          </div>
          <div>
            <label for="ui-state-filled">Filled</label>
            <Input id="ui-state-filled" value="Studio workspace" />
            <small>Native editable text input.</small>
          </div>
          <div>
            <label for="ui-state-invalid">Invalid example</label>
            <Input
              id="ui-state-invalid"
              type="email"
              value="hello@"
              aria-invalid="true"
              aria-describedby="ui-state-error"
            />
            <small id="ui-state-error" class="ui-error-text">
              Enter a complete email address.
            </small>
          </div>
          <div>
            <label for="ui-state-disabled">Disabled</label>
            <Input id="ui-state-disabled" value="zyra-workspace" disabled />
            <small>Unavailable fields stay recognizable.</small>
          </div>
        </div>
        <label class="ui-checkbox-example">
          <Input type="checkbox" />
          Enable project notifications
        </label>
      </UIPreview>

      <UIPreview
        id="ui-cards"
        title="Cards"
        description="Bring content, status, and actions into a reusable surface."
        code={
          'import { Card, Badge, Button } from "@zyra/ui";\n\n<Card class="project-card">\n  <Badge variant="success">Active</Badge>\n  <h3>Studio workspace</h3>\n  <p>A home for your next project.</p>\n  <Button variant="secondary">View project</Button>\n</Card>'
        }
      >
        <div class="ui-card-grid">
          <Card class="ui-project-card">
            <div class="ui-card-heading">
              <span class="ui-project-symbol" aria-hidden="true">
                ↗
              </span>
              <Badge variant="success">Active</Badge>
            </div>
            <h3>Studio workspace</h3>
            <p>A little structure for your next big idea.</p>
            <div class="ui-project-meta">
              <span>Design system</span>
              <span>12 components</span>
            </div>
          </Card>
          <Card class="ui-project-card">
            <div class="ui-card-heading">
              <span class="ui-project-symbol" aria-hidden="true">
                ⌁
              </span>
              <Badge>Draft</Badge>
            </div>
            <h3>Something new</h3>
            <p>Start with a surface. Make it your own.</p>
            <div class="ui-project-meta">
              <span>Personal project</span>
              <span>Just started</span>
            </div>
          </Card>
        </div>
      </UIPreview>

      <UIPreview
        id="ui-composition"
        title="Put it together"
        description="Input, select, textarea, buttons, and a real modal dialog."
        code={
          'import { signal } from "zyra";\nimport { Button, Dialog } from "@zyra/ui";\n\nfunction DialogExample() {\n  const open = signal(false);\n  return <div>\n    <Button onClick={() => { open.value = true; }}>Open dialog</Button>\n    <Dialog open={open.value}\n      onOpenChange={value => { open.value = value; }}\n      aria-labelledby="preview-title">\n      <h2 id="preview-title">A space for your ideas</h2>\n      <Button autofocus onClick={() => { open.value = false; }}>\n        Got it\n      </Button>\n    </Dialog>\n  </div>;\n}'
        }
      >
        <UIFormExample />
      </UIPreview>

      <section class="ui-token-strip" aria-label="Current theme tokens">
        <div>
          <i class="ui-token-accent" />
          <span>
            Accent<small>--zy-accent</small>
          </span>
        </div>
        <div>
          <i class="ui-token-surface" />
          <span>
            Surface<small>--zy-surface</small>
          </span>
        </div>
        <div>
          <i class="ui-token-muted" />
          <span>
            Muted<small>--zy-muted</small>
          </span>
        </div>
        <div>
          <i class="ui-token-text" />
          <span>
            Text<small>--zy-text</small>
          </span>
        </div>
      </section>
    </div>
  );
}
